import { Link } from "react-router-dom";
import Tooltip from "../Tooltip";
import { useEffect, useRef, useState } from "react";
import { useAccount } from "@contexts/AccountContext";
import { useWallet } from "@providers/wallet.provider";

function ProfileDropdown() {
  const { user } = useAccount();
  const { disconnect } = useWallet();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + ESC
  useEffect(() => {
    const onDocClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (!btnRef.current || !menuRef.current) return;
      if (
        !btnRef.current.contains(target) &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Lock body scroll when open (nice for mobile)
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group ml-2 flex h-10 w-10 items-center justify-center rounded-full border border-jacarta-100 bg-white transition-colors hover:border-transparent hover:bg-accent focus:border-transparent focus:bg-accent dark:border-transparent dark:bg-white/[.15] dark:hover:bg-jacarta-500"
        id="profileDropdown"
        aria-expanded={open}
        aria-controls="profileDropdownMenu"
        aria-label="profile"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          className="h-4 w-4 fill-jacarta-700 transition-colors group-hover:fill-white group-focus:fill-white dark:fill-white"
        >
          <path fill="none" d="M0 0h24v24H0z" />
          <path d="M11 14.062V20h2v-5.938c3.946.492 7 3.858 7 7.938H4a8.001 8.001 0 0 1 7-7.938zM12 13c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6z" />
        </svg>
      </button>

      {/* Mobile overlay (tap to close) */}
      <div
        className={`${
          open
            ? "bg-black/20 fixed inset-0 z-40 backdrop-blur-[1px] lg:hidden"
            : "hidden"
        }`}
        onClick={() => setOpen(false)}
      />

      <div
        ref={menuRef}
        id="profileDropdownMenu"
        role="menu"
        aria-labelledby="profileDropdown"
        className={`/* Mobile: sheet-style */ /* Desktop: classic
          dropdown */ fixed inset-x-4
          top-16 z-50 max-h-[70vh] overflow-y-auto rounded-xl bg-white
          p-3 shadow-2xl transition-all will-change-transform dark:bg-gray-900
          lg:absolute lg:left-auto lg:right-0 lg:top-[85%] lg:w-auto lg:min-w-[14rem] lg:p-4
          ${
            open
              ? "translate-y-0 opacity-100"
              : "pointer-events-none -translate-y-1 opacity-0"
          }`}
      >
        <button
          className="my-3 flex w-full select-none items-center whitespace-nowrap px-5 font-display leading-none text-jacarta-700 dark:text-white"
          onClick={() => {
            navigator.clipboard?.writeText(user?.walletAddress ?? "");
            setOpen(false);
          }}
        >
          <span className="max-w-[16rem] overflow-hidden text-ellipsis lg:max-w-[10rem]">
            {user?.walletAddress}
          </span>
        </button>

        <div className="mx-5 mb-4 rounded-lg border border-jacarta-100 p-4 dark:border-jacarta-600">
          <span className="text-sm font-medium tracking-tight dark:text-jacarta-200">
            Welcome
          </span>
          <div className="mt-1 flex items-center">
            <span className="text-lg font-bold text-green">
              {user?.username ?? "Unknown"}
            </span>
          </div>
        </div>

        <Link
          to="/profile"
          onClick={() => setOpen(false)}
          className="flex items-center space-x-2 rounded-xl px-5 py-3 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-600 lg:py-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            className="h-5 w-5 fill-jacarta-700 transition-colors dark:fill-white lg:h-4 lg:w-4"
          >
            <path fill="none" d="M0 0h24v24H0z"></path>
            <path d="M11 14.062V20h2v-5.938c3.946.492 7 3.858 7 7.938H4a8.001 8.001 0 0 1 7-7.938zM12 13c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6z"></path>
          </svg>
          <span className="mt-0.5 font-display text-sm text-jacarta-700 dark:text-white">
            My Profile
          </span>
        </Link>

        <Link
          to="/profile/edit"
          onClick={() => setOpen(false)}
          className="flex items-center space-x-2 rounded-xl px-5 py-3 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-600 lg:py-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            className="h-5 w-5 fill-jacarta-700 transition-colors dark:fill-white lg:h-4 lg:w-4"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path d="M9.954 2.21a9.99 9.99 0 0 1 4.091-.002A3.993 3.993 0 0 0 16 5.07a3.993 3.993 0 0 0 3.457.261A9.99 9.99 0 0 1 21.5 8.876 3.993 3.993 0 0 0 20 12c0 1.264.586 2.391 1.502 3.124a10.043 10.043 0 0 1-2.046 3.543 3.993 3.993 0 0 0-3.456.261 3.993 3.993 0 0 0-1.954 2.86 9.99 9.99 0 0 1-4.091.004A3.993 3.993 0 0 0 8 18.927a3.993 3.993 0 0 0-3.993-.26A9.99 9.99 0 0 1 2.5 15.121 3.993 3.993 0 0 0 4 11.999a3.993 3.993 0 0 0-1.502-3.124 10.043 10.043 0 0 1 2.046-3.543A3.993 3.993 0 0 0 8 5.071a3.993 3.993 0 0 0 1.954-2.86zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
          </svg>
          <span className="mt-0.5 font-display text-sm text-jacarta-700 dark:text-white">
            Edit Profile
          </span>
        </Link>

        <button
          type="button"
          onClick={() => {
            disconnect();
            setOpen(false);
          }}
          className="flex w-full items-center space-x-2 rounded-xl px-5 py-3 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-600 lg:py-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            className="h-5 w-5 fill-jacarta-700 transition-colors dark:fill-white lg:h-4 lg:w-4"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zM7 11V8l-5 4 5 4v-3h8v-2H7z" />
          </svg>
          <span className="mt-0.5 font-display text-sm text-jacarta-700 dark:text-white">
            Disconnect
          </span>
        </button>
      </div>
    </>
  );
}

export default ProfileDropdown;
